import React, { useState } from 'react';
import { Menu, User, Search, X, Brain, RefreshCw, Home, LogOut, ShoppingCart, ChevronDown } from 'lucide-react';

interface HeaderProps {
  currentView: string;
  onViewChange: (view: string) => void;
  hasProfile?: boolean;
}

export default function Header({ currentView, onViewChange, hasProfile = false }: HeaderProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navItems = [
    { 
      id: 'dashboard', 
      label: 'Dashboard', 
      requiresProfile: true,
      description: 'Your DrizzID™ overview',
      icon: User
    },
    { 
      id: 'profile', 
      label: 'Profile', 
      requiresProfile: true,
      description: 'Manage your taste profile',
      icon: User
    },
    { 
      id: 'shop', 
      label: 'Shop', 
      requiresProfile: false,
      description: 'Browse sauce collection',
      icon: ShoppingCart
    },
    { 
      id: 'discover', 
      label: 'Discover', 
      requiresProfile: true,
      description: 'Find new flavors',
      icon: Search
    }
  ];

  const handleNavClick = (view: string) => {
    onViewChange(view);
    setIsMobileMenuOpen(false);
  };

  const handleLogoClick = () => {
    // Navigate back to the main waitlist page
    onViewChange('waitlist');
    setIsMobileMenuOpen(false);
  };

  const handleRetakeQuiz = () => {
    onViewChange('quiz');
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <style jsx>{`
        .mobile-menu-backdrop {
          backdrop-filter: blur(8px);
          background: rgba(0, 0, 0, 0.5);
        }
        
        .mobile-menu-slide {
          transform: translateX(100%);
          transition: transform 0.3s ease-in-out;
        }
        
        .mobile-menu-slide.open {
          transform: translateX(0);
        }
        
        .nav-item-hover {
          transition: all 0.2s ease;
        }
        
        .nav-item-hover:hover {
          background: rgba(255, 255, 255, 0.1);
          transform: translateX(4px);
        }
        
        .nav-item-active {
          background: linear-gradient(135deg, #ec4899, #8b5cf6);
          box-shadow: 0 4px 12px rgba(236, 72, 153, 0.3);
        }
        
        .nav-item-disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        
        .nav-item-disabled:hover {
          background: transparent;
          transform: none;
        }
        
        @media (max-width: 1024px) {
          .desktop-nav {
            display: none;
          }
        }
        
        @media (min-width: 1025px) {
          .mobile-nav {
            display: none;
          }
        }
      `}</style>

      <header className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-md border-b border-pink-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            {/* Enhanced Logo - Always Clickable */}
            <div className="flex items-center space-x-2 sm:space-x-4">
              <img 
                src="/logo transparent with tagline.png" 
                alt="UNDRESSING Logo" 
                className="h-10 sm:h-12 lg:h-16 w-auto object-contain cursor-pointer transition-all duration-300 hover:scale-105 hover:opacity-80"
                onClick={handleLogoClick}
                title="Back to UNDRESSING Homepage"
              />
            </div>

            {/* Desktop Navigation */}
            <nav className="desktop-nav hidden lg:flex items-center space-x-4 xl:space-x-6">
              {navItems.map((item) => {
                const isDisabled = item.requiresProfile && !hasProfile;
                const ItemIcon = item.icon;
                return (
                  <button
                    key={item.id}
                    onClick={() => !isDisabled && onViewChange(item.id)}
                    disabled={isDisabled}
                    className={`px-3 xl:px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 relative group flex items-center space-x-2 ${
                      currentView === item.id
                        ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg shadow-pink-500/25'
                        : isDisabled
                        ? 'text-gray-500 cursor-not-allowed'
                        : 'text-gray-300 hover:text-white hover:bg-white/10'
                    }`}
                    title={isDisabled ? 'Complete DrizzID™ quiz first' : item.description}
                  >
                    <ItemIcon className="h-4 w-4" />
                    <span>{item.label}</span>
                    {isDisabled && (
                      <div className="absolute -top-1 -right-1 w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                    )}
                  </button>
                );
              })}
              
              {/* Retake Quiz Button */}
              {hasProfile && (
                <button
                  onClick={handleRetakeQuiz}
                  className="flex items-center space-x-2 px-4 py-2 border border-cyan-500/50 text-cyan-400 rounded-full font-medium hover:bg-cyan-500/10 transition-all duration-300"
                  title="Retake DrizzID™ quiz"
                >
                  <RefreshCw className="h-4 w-4" />
                  <span>Retake Quiz</span>
                </button>
              )}
            </nav>

            {/* Mobile Navigation Button */}
            <div className="mobile-nav lg:hidden flex items-center space-x-3">
              {/* Profile Status Indicator - Mobile */}
              {hasProfile && (
                <div className="bg-green-500/20 border border-green-500/30 text-green-400 px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1">
                  <div className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></div>
                  <span>Active</span>
                </div>
              )}
              
              <button 
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-white/10 relative"
              >
                {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                {/* Notification dot for disabled items */}
                {!hasProfile && (
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-500 rounded-full animate-pulse"></div>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <div 
              className="mobile-nav lg:hidden fixed inset-0 mobile-menu-backdrop z-40"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            
            {/* Slide-out Menu */}
            <div className={`mobile-nav lg:hidden fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-gray-900/95 backdrop-blur-md border-l border-pink-500/20 z-50 mobile-menu-slide ${isMobileMenuOpen ? 'open' : ''}`}>
              <div className="flex flex-col h-full">
                {/* Menu Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-700">
                  <div className="flex items-center space-x-3">
                    <img 
                      src="/logo transparent with tagline.png" 
                      alt="UNDRESSING Logo" 
                      className="h-8 w-auto object-contain"
                    />
                  </div>
                  <button
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="p-2 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-white/10"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                {/* Profile Status */}
                <div className="p-6 border-b border-gray-700">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className={`w-3 h-3 rounded-full ${hasProfile ? 'bg-green-500' : 'bg-yellow-500'} animate-pulse`}></div>
                    <span className="text-white font-medium">
                      {hasProfile ? 'DrizzID™ Profile Active' : 'No DrizzID™ Profile'}
                    </span>
                  </div>
                  {!hasProfile && (
                    <p className="text-gray-400 text-sm">
                      Complete the quiz to unlock all features
                    </p>
                  )}
                </div>

                {/* Navigation Items */}
                <div className="flex-1 overflow-y-auto p-6">
                  <div className="space-y-3">
                    {/* Back to Homepage */}
                    <button
                      onClick={handleLogoClick}
                      className="nav-item-hover w-full text-left px-4 py-4 rounded-xl text-pink-400 hover:text-pink-300 border border-pink-500/30 flex items-center space-x-3"
                    >
                      <Home className="h-5 w-5" />
                      <div>
                        <div className="font-medium">← UNDRESSING Homepage</div>
                        <div className="text-xs text-gray-400 mt-1">Back to main site</div>
                      </div>
                    </button>
                    
                    {navItems.map((item) => {
                      const isDisabled = item.requiresProfile && !hasProfile;
                      const isActive = currentView === item.id;
                      const ItemIcon = item.icon;
                      
                      return (
                        <button
                          key={item.id}
                          onClick={() => !isDisabled && handleNavClick(item.id)}
                          disabled={isDisabled}
                          className={`nav-item-hover w-full text-left px-4 py-4 rounded-xl font-medium transition-all duration-300 relative flex items-center space-x-3 ${
                            isActive
                              ? 'nav-item-active text-white'
                              : isDisabled
                              ? 'nav-item-disabled text-gray-500 bg-gray-800/30'
                              : 'text-gray-300 hover:text-white'
                          }`}
                        >
                          <ItemIcon className="h-5 w-5 flex-shrink-0" />
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <span>{item.label}</span>
                              {isDisabled && (
                                <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                              )}
                              {isActive && (
                                <div className="w-2 h-2 bg-white rounded-full"></div>
                              )}
                            </div>
                            <div className="text-xs text-gray-400 mt-1">
                              {isDisabled ? 'Complete DrizzID™ quiz first' : item.description}
                            </div>
                          </div>
                          {isActive && (
                            <ChevronDown className="h-4 w-4 text-white/60 rotate-[-90deg]" />
                          )}
                        </button>
                      );
                    })}
                    
                    {/* Retake Quiz Button - Mobile */}
                    {hasProfile && (
                      <button
                        onClick={handleRetakeQuiz}
                        className="nav-item-hover w-full text-left px-4 py-4 rounded-xl font-medium transition-all duration-300 border border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10 flex items-center space-x-3"
                      >
                        <RefreshCw className="h-5 w-5" />
                        <div>
                          <div>Retake DrizzID™ Quiz</div>
                          <div className="text-xs text-gray-400 mt-1">Update your taste profile</div>
                        </div>
                      </button>
                    )}
                  </div>
                </div>

                {/* Menu Footer */}
                <div className="p-6 border-t border-gray-700">
                  <div className="text-center">
                    <p className="text-gray-400 text-sm mb-2">UNDRESSING™ Sauce</p>
                    <p className="text-pink-400 text-xs font-semibold">Strip. Drip. Glow™</p>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </header>
    </>
  );
}